import React from "react";
import DynamicField from "./DynamicField";

interface DynamicSectionProps {
  section: any;
  sectionData?: any;
  onChange: (sectionId: string, fieldId: string, value: any) => void;
  errors?: Record<string, any>;
  applicationId: string;
  disabled?: boolean;
  folder?: string;
  recruitment: any;
}

export default function DynamicSection({
  section,
  sectionData = {},
  onChange,
  errors = {},
  applicationId,
  disabled = false,
  folder = "applications",
  recruitment,
}: DynamicSectionProps) {
  const { id: sectionId, title, description, fields } = section;

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange(sectionId, fieldId, value);
  };

  // Extract errors belonging to this section: field errors have key "sectionId.fieldId" or "sectionId.fieldId.subPath"
  const getFieldErrors = (fieldId: string) => {
    const prefix = `${sectionId}.${fieldId}`;
    let fieldError: any = null;

    // Check for exact path error (e.g. "personalInfo.name")
    if (errors[prefix]) {
      fieldError = errors[prefix];
    } else {
      // Check for nested path errors (e.g. "personalInfo.currentAddress.city" or "qualifications.qualificationsList.0.degree")
      const nestedErrors: Record<string, any> = {};
      let hasNested = false;
      Object.keys(errors).forEach((key) => {
        if (key.startsWith(`${prefix}.`)) {
          const relativePath = key.substring(prefix.length + 1); // e.g. "city" or "0.degree"
          nestedErrors[relativePath] = errors[key];
          hasNested = true;
        }
      });
      if (hasNested) {
        fieldError = nestedErrors;
      }
    }

    return fieldError;
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-6 shadow-sm">
      <div className="border-b border-gray-150 pb-4">
        <h3 className="text-xl font-bold text-gray-900">{title}</h3>
        {description && <p className="text-sm text-gray-500 mt-1">{description}</p>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {fields.map((field: any) => {
          // Address, repeater, textarea, or checkbox can span full width on grid
          const isFullWidth =
            field.type === "address" ||
            field.type === "repeater" ||
            field.type === "textarea" ||
            field.id === "name"; // let Name span full width

          const colSpanClass = isFullWidth ? "md:col-span-2" : "col-span-1";

          return (
            <div key={field.id} className={colSpanClass}>
              <DynamicField
                field={field}
                value={sectionData[field.id]}
                onChange={handleFieldChange}
                error={getFieldErrors(field.id)}
                applicationId={applicationId}
                disabled={disabled}
                folder={folder}
                recruitment={recruitment}
                sectionData={sectionData}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
